import { Button, Link, Popover, Skeleton, Stack, Typography } from '@mui/material';

import first from 'lodash/first';
import sortBy from 'lodash/sortBy';

import PlusIcon from '@/components/Icons/PlusIcon';
import SettingsIcon from '@/components/Icons/SettingsIcon';
import UserPlusIcon from '@/components/Icons/UserPlusIcon';
import { TeamMemberTypeRoleEnum } from '@/graphql';
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin';
import { links } from '@/localization';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import { Avatar } from '@/sections/GlobalHeader/Left/WorkspaceLink/styled';
import { pluralizeString } from '@/utils/pluralizeString';
import { useDialogs } from '@toolpad/core/useDialogs';

import AddMember from '../AddMember';
import CreateTeam from '../CreateTeam';
import TeamName from '../TeamName';
import { useTeam } from '../hooks';
import { MenuItem, TeamResource, TeamResourceName } from '../styled';
import { Container, Section } from './styled';
import { ManagePopoverProps } from './types';

function ManagePopover({
  isMultipleTeamsEnabled,
  onClose,
  anchorEl,
  teams,
  selectedTeamId,
  onSelectTeam,
}: ManagePopoverProps) {
  const goToPage = usePageSwitcher();
  const dialogs = useDialogs();
  const { team: selectedTeam, loading, members, userRoleTitle, userRole } = useTeam(selectedTeamId);
  const isOrgAdmin = useIsOrgAdmin();

  function goToTeamSettingsPage() {
    goToPage(BasetenPageEnum.TeamSettings, { teamId: selectedTeamId });
  }

  function getHeader() {
    if (loading) {
      return (
        <Stack gap={0.5} height={35}>
          <Skeleton animation="wave" width={32} height={32} />
          <Skeleton animation="wave" width={100} height={20} />
        </Stack>
      );
    }

    return (
      <Stack direction="row" gap={0.5} alignItems="center" height={35}>
        <Avatar emoji={!!selectedTeam.icon} sx={{ width: 32, height: 32 }}>
          {selectedTeam.icon || first(selectedTeam.name)}
        </Avatar>
        <Stack gap={0.25}>
          <Typography variant="h5">{selectedTeam.name}</Typography>
          <Stack direction="row" gap={0.25}>
            <Typography variant="body2" color="text.muted">
              {userRoleTitle}
            </Typography>
            <Typography variant="body2" color="text.muted">
              ·
            </Typography>
            <Typography variant="body2" color="text.muted">
              {pluralizeString(members.length, 'member')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  function getContent() {
    if (isMultipleTeamsEnabled) {
      return (
        <Stack gap={0.2}>
          {sortBy(teams || [], (team) => !team.default)?.map((team) => (
            <MenuItem
              key={team.id}
              onClick={() => onSelectTeam(team.id)}
              selected={team.id === selectedTeamId}
              sx={{ p: 0.5 }}
            >
              <TeamName
                name={team.name}
                icon={team.icon}
                maxWidth={170}
                suffix={team.default && <Typography variant="body1">(default)</Typography>}
              />
            </MenuItem>
          ))}
        </Stack>
      );
    }

    return (
      <Stack
        sx={{
          py: 1,
          pt: 2,
          px: 1,
          gap: 2,
        }}
      >
        <Typography variant="h5">Upgrade to Enterprise</Typography>
        <Typography variant="body2" color="text.muted">
          Create teams to assign roles.{' '}
          <Link href={links.basetenContactUs} target="_blank">
            Learn more
          </Link>
        </Typography>
        <Button variant="outlined" color="primary" href={links.basetenContactUs} target="_blank">
          Talk to sales
        </Button>
      </Stack>
    );
  }

  return (
    <Popover open={!!anchorEl} onClose={() => onClose()} anchorEl={anchorEl}>
      <Container>
        <Section gap={1}>
          {getHeader()}
          <Stack direction="row" gap={1} alignItems="center">
            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={goToTeamSettingsPage}>
              Manage
            </Button>
            {(isOrgAdmin || TeamMemberTypeRoleEnum.TeamAdmin === userRole) && (
              <Button
                variant="outlined"
                startIcon={<UserPlusIcon />}
                onClick={() => dialogs.open(AddMember) && onClose()}
              >
                Add
              </Button>
            )}
          </Stack>
        </Section>
        <Section gap={1}>
          <Typography variant="h6" color="text.muted">
            Switch teams
          </Typography>
          {getContent()}
        </Section>
        {isMultipleTeamsEnabled && isOrgAdmin && (
          <Section>
            <MenuItem sx={{ p: 1 }} onClick={() => dialogs.open(CreateTeam) && onClose()}>
              <TeamResource>
                <TeamResourceName>
                  <PlusIcon /> Create new team
                </TeamResourceName>
              </TeamResource>
            </MenuItem>
          </Section>
        )}
      </Container>
    </Popover>
  );
}

export default ManagePopover;
